/**
 * Turning the QR already on screen into a file the restaurant can hand to a
 * printer, entirely in the browser.
 *
 * **Nothing is stored.** The token is minted server-side and never changes, so
 * an image of it is a pure function of what the page already holds — writing a
 * PNG into object storage would be a second copy of a fact the invite row
 * already carries, one that goes stale the moment the invite is revoked and
 * that nobody would think to delete.
 *
 * The source is the `<svg>` `react-qr-code` rendered, read back out of the DOM
 * rather than re-encoded. Re-encoding would mean a second QR library and a
 * second answer to "what does this URL look like as modules": the paper on the
 * table has to be the thing the screen showed, not a lookalike computed twice.
 *
 * ## Why the file is a sheet and not a bare QR
 *
 * The print path this replaces deliberately put the restaurant's name and the
 * table's name under the code, and said why: a QR alone, glued to a table, says
 * nothing about which restaurant or which table it belongs to — which is
 * exactly the question someone asks when they find a loose one in a drawer six
 * months later. Downloading the code on its own would have dropped that, so the
 * exported sheet composes the same three things the printed one did.
 */

/** The sheet, in SVG user units. A 5:6 portrait that prints on anything. */
const SHEET_WIDTH = 600
const SHEET_HEIGHT = 760
const QR_INSET = 60
const QR_SIZE = SHEET_WIDTH - QR_INSET * 2

/**
 * How many device pixels per SVG unit the raster formats get. At 3 the sheet
 * lands at 1800×2280, which survives being printed at letter size; at 1 it is
 * a QR that fails to scan from a phone held at arm's length.
 */
const RASTER_SCALE = 3

/**
 * JPEG's quality knob. High enough that the module edges stay crisp — a QR is
 * the worst case for a DCT codec, and ringing around the corner markers is what
 * makes a scan fail.
 */
const JPEG_QUALITY = 0.95

export const QR_DOWNLOAD_FORMATS = ["png", "jpg", "svg"] as const

export type QrDownloadFormat = (typeof QR_DOWNLOAD_FORMATS)[number]

const MIME_TYPES: Record<QrDownloadFormat, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  svg: "image/svg+xml",
}

/**
 * The five characters that cannot travel as themselves inside an SVG attribute
 * or text node. A restaurant called "Tacos & Salsa" is the ordinary case, not
 * the exotic one, and an unescaped ampersand makes the whole document
 * unparseable — which shows up as a blank PNG rather than as an error.
 */
function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;")
}

/**
 * A filename someone can find again: the restaurant, the table, and what it is.
 * Folded to ASCII-ish because this string reaches a filesystem, a Windows
 * printer queue and an email attachment, and each of those mangles a different
 * subset of everything else.
 */
export function qrFileName(locationName: string, tableName: string, format: QrDownloadFormat): string {
  const slug = (value: string) =>
    value
      .normalize("NFD")
      // NFD splits "í" into "i" plus a combining accent; this drops the accent
      // and keeps the letter. Named rather than written as a `̀-ͯ`
      // range: that range is invisible in a source file, so a stray
      // normalisation pass by an editor would quietly turn it into a character
      // class of nothing and the folding would stop happening with every test
      // still green.
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")

  return [ "qr", slug(locationName), slug(tableName) ].filter(Boolean).join("-") + `.${format}`
}

export type QrSheetSource = {
  /** The `viewBox` of the QR `<svg>` on screen, e.g. `"0 0 29 29"`. */
  viewBox: string
  /** That svg's `innerHTML` — the module paths, verbatim. */
  markup: string
  locationName: string
  tableName: string
}

/**
 * The printable sheet as a standalone SVG document.
 *
 * The QR travels as a NESTED `<svg>` carrying its own viewBox rather than as
 * scaled paths: the browser then does the fitting with the same arithmetic it
 * already did on screen, so a 29-module code and a 33-module one both land
 * square in the same box with no per-version maths here.
 *
 * The font stack ends in `sans-serif` on purpose. This string is rasterised by
 * `<img>`, which resolves fonts against the document but cannot fetch a webfont
 * it has never seen; a stack with no generic fallback renders the two labels in
 * whatever the platform defaults to, which on some Androids is nothing at all.
 */
export function buildQrSheetSvg({ viewBox, markup, locationName, tableName }: QrSheetSource): string {
  const labelY = QR_INSET + QR_SIZE + 90
  const fontStack = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${SHEET_WIDTH}" height="${SHEET_HEIGHT}" viewBox="0 0 ${SHEET_WIDTH} ${SHEET_HEIGHT}">`,
    // Painted rather than left transparent. PNG would carry the alpha and print
    // fine, but JPEG has no alpha at all and fills it with black, which is a QR
    // nothing can read.
    `<rect width="${SHEET_WIDTH}" height="${SHEET_HEIGHT}" fill="#ffffff"/>`,
    `<svg x="${QR_INSET}" y="${QR_INSET}" width="${QR_SIZE}" height="${QR_SIZE}" viewBox="${escapeXml(viewBox)}">${markup}</svg>`,
    `<text x="${SHEET_WIDTH / 2}" y="${labelY}" text-anchor="middle" font-family="${fontStack}" font-size="34" font-weight="600" fill="#0a0a0a">${escapeXml(locationName)}</text>`,
    `<text x="${SHEET_WIDTH / 2}" y="${labelY + 44}" text-anchor="middle" font-family="${fontStack}" font-size="26" fill="#525252">${escapeXml(tableName)}</text>`,
    `</svg>`,
  ].join("")
}

/** Reads the on-screen QR back out of the DOM, or null if it never rendered. */
export function readQrSvg(container: HTMLElement | null): { viewBox: string; markup: string } | null {
  const svg = container?.querySelector("svg")
  if (!svg) return null

  const viewBox = svg.getAttribute("viewBox")
  if (!viewBox) return null

  return { viewBox, markup: svg.innerHTML }
}

function rasterise(sheet: string, format: "png" | "jpg"): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const image = new Image()

    image.onload = () => {
      const canvas = document.createElement("canvas")
      canvas.width = SHEET_WIDTH * RASTER_SCALE
      canvas.height = SHEET_HEIGHT * RASTER_SCALE

      const context = canvas.getContext("2d")
      if (!context) return reject(new Error("canvas unavailable"))

      // Twice: the sheet already paints its own background, and a canvas starts
      // transparent-black. Both matter for JPEG and neither costs anything.
      context.fillStyle = "#ffffff"
      context.fillRect(0, 0, canvas.width, canvas.height)
      context.drawImage(image, 0, 0, canvas.width, canvas.height)

      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("encode failed"))),
        MIME_TYPES[format],
        format === "jpg" ? JPEG_QUALITY : undefined
      )
    }
    image.onerror = () => reject(new Error("svg could not be rasterised"))

    // A data URL rather than a blob URL: a blob URL taints nothing either, but
    // it has to be revoked, and the failure of forgetting is a leak that only
    // shows up on the tablet that has been open since Tuesday.
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(sheet)}`
  })
}

export async function qrSheetBlob(source: QrSheetSource, format: QrDownloadFormat): Promise<Blob> {
  const sheet = buildQrSheetSvg(source)

  if (format === "svg") return new Blob([ sheet ], { type: MIME_TYPES.svg })

  return rasterise(sheet, format)
}

/** Hands the file to the browser's own download machinery and cleans up after it. */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")

  anchor.href = url
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()

  // Deferred, not immediate: revoking inside the same tick cancels the download
  // it was just handed to in Safari.
  setTimeout(() => URL.revokeObjectURL(url), 0)
}
